// Kleine bouwstenen van de projectenlijst: signaal, periodebalk, urenmeter,
// takenteller, deadline en PL. Gedeeld door tabel en kaarten.
//
// Rust (25 sep): één accentkleur (primary) plus neutrale tinten. Rood/oranje
// alleen voor het ene signaal per rij (Signaal); de rest blijft grijs. Drie
// tekstgroottes: T.groot (projectnaam), T.normaal (waarden), T.klein (meta/kop).
import React from 'react';
import { Box, Typography, Tooltip, Avatar } from '@mui/material';
import {
  GEZONDHEID, RECENT_DAGEN, UREN_RISICO, DEADLINE_RISICO_DAGEN, UREN_PER_DAG,
  korteDatum, relatief, formatUren, dagenTussen, vandaagDag, signaalVan, urenKleur,
} from './projectGezondheid';

export const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  line: 'rgba(45,51,53,0.07)',
  rood: '#dc2626', oranje: '#d97706', groen: '#16a34a',
  // Urenbalk: gedempte tinten, zoals de balk in Gripp (Joris 22 sep).
  urenGroen: '#6cbf98', urenOranje: '#e0a03a', urenRood: '#d9534f',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
  groot: '0.84rem', normaal: '0.74rem', klein: '0.68rem',
  balk: '#e3e7ea', balkVol: '#b4bbc0',
};

export const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const tipSx = { tooltip: { sx: { fontFamily: T.fontBody, fontSize: '0.72rem', bgcolor: T.text, borderRadius: '0.5rem', maxWidth: 340 } }, arrow: { sx: { color: T.text, '&::before': { border: 'none' } } } };
export const Tip = ({ title, children }) => (
  <Tooltip title={title} arrow slotProps={tipSx}>{children}</Tooltip>
);

export const getInitials = (name) => { if (!name) return '?'; const p = String(name).split(' '); return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : String(name).substring(0, 2).toUpperCase(); };

// ── Signaal (één per rij) ──────────────────────────────────────────────────
export const REGEL_UITLEG = (
  <Box sx={{ lineHeight: 1.5 }}>
    <b>Te laat</b> (rood) — deliverable-deadline verstreken, uren boven budget, of 3+ taken te laat.<br />
    <b>Risico</b> (oranje) — 1–2 taken te laat, ≥ {Math.round(UREN_RISICO * 100)}% van de uren op, of een deliverable-deadline binnen {DEADLINE_RISICO_DAGEN} dagen.<br />
    <b>Op schema</b> — geen van beide; de rij blijft dan leeg.<br />
    <b>Geen data</b> — geen urenbudget in Gripp en geen gedateerde taken, of de taken konden niet worden opgehaald.<br />
    <span style={{ opacity: 0.75 }}>Alleen achterstand van de laatste {RECENT_DAGEN} dagen telt; oudere open items zijn meestal vergeten vinkjes.</span>
  </Box>
);

const statusTip = (gezondheid) => {
  const st = GEZONDHEID[gezondheid?.status] || GEZONDHEID.onbekend;
  return (
    <Box>
      <Box sx={{ fontWeight: 700, mb: 0.5 }}>{st.label}</Box>
      {gezondheid?.redenen?.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2, mb: 0.75 }}>
          {gezondheid.redenen.map((r) => <li key={r.tekst}>{r.tekst}</li>)}
        </Box>
      )}
      <Box sx={{ opacity: 0.85, borderTop: '1px solid rgba(255,255,255,0.15)', pt: 0.75 }}>{REGEL_UITLEG}</Box>
    </Box>
  );
};

// Tekst van de zwaarste reden in rood (te laat) of oranje (risico); gezonde
// projecten tonen niets. `metStatus` toont in rust de status als grijs woord (kaart).
export function Signaal({ gezondheid, metStatus = false }) {
  if (gezondheid === null) return <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.outlineVariant }}>…</Typography>;
  const sig = signaalVan(gezondheid);
  if (!sig && !metStatus) {
    // Stil, maar de uitleg blijft op hover bereikbaar.
    return (
      <Tip title={statusTip(gezondheid)}>
        <Box sx={{ height: 16, width: '100%', cursor: 'help' }} onClick={(e) => e.stopPropagation()} />
      </Tip>
    );
  }
  const st = GEZONDHEID[gezondheid?.status] || GEZONDHEID.onbekend;
  const kleur = sig ? (sig.ernst === 'te_laat' ? T.rood : T.oranje) : T.outline;
  return (
    <Tip title={statusTip(gezondheid)}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0, cursor: 'help' }} onClick={(e) => e.stopPropagation()}>
        <Typography component="span" noWrap sx={{ fontFamily: T.fontBody, fontSize: T.normaal, fontWeight: sig ? 600 : 400, color: kleur, minWidth: 0 }}>
          {sig ? sig.tekst : st.label}
        </Typography>
        {sig?.meer > 0 && (
          <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline, flexShrink: 0 }}>+{sig.meer}</Typography>
        )}
      </Box>
    </Tip>
  );
}

// ── Periodebalk ────────────────────────────────────────────────────────────
// Met `venster` ({van, tot}) delen alle rijen één tijdas (tabel); zonder venster
// schaalt de balk op het eigen project (kaart). Neutraal grijs: het verstreken
// deel iets donkerder, vandaag als dunne accentlijn. Status zit in het Signaal.
export function PeriodeBalk({ gezondheid, venster = null, hoogte = 4 }) {
  const vandaag = vandaagDag();
  const { start, eind, mijlpalen = [] } = gezondheid || {};
  if (!start && !eind) {
    return <Typography sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outlineVariant }}>geen planning</Typography>;
  }
  const van = venster ? venster.van : (start || vandaag);
  const tot = venster ? venster.tot : (eind || vandaag);
  const lo = new Date(Math.min(van.getTime(), vandaag.getTime(), ...(venster ? [] : [start, eind].filter(Boolean).map((d) => d.getTime()))));
  const hi = new Date(Math.max(tot.getTime(), vandaag.getTime(), ...(venster ? [] : [start, eind].filter(Boolean).map((d) => d.getTime()))));
  const bereik = venster ? dagenTussen(venster.van, venster.tot) : Math.max(1, dagenTussen(lo, hi));
  const basis = venster ? venster.van : lo;
  const pos = (d) => Math.max(0, Math.min(100, (dagenTussen(basis, d) / bereik) * 100));
  const s = start ? pos(start) : 0;
  const e = eind ? pos(eind) : 100;
  const nu = pos(vandaag);
  const verlopen = Math.max(0, Math.min(e, nu) - s);
  const tip = (
    <Box>
      <div>Start: {start ? korteDatum(start, vandaag) : 'onbekend'}</div>
      <div>Oplevering: {eind ? `${korteDatum(eind, vandaag)} (${relatief(dagenTussen(vandaag, eind))})` : 'onbekend'}</div>
      {mijlpalen.length > 0 && <Box sx={{ mt: 0.5 }}>{mijlpalen.slice(0, 6).map((m, i) => <div key={`${i}-${m.dag.getTime()}`}>◆ {korteDatum(m.dag, vandaag)} · {m.naam}{m.teLaat ? ' (te laat)' : ''}</div>)}</Box>}
      <Box sx={{ mt: 0.5, opacity: 0.7 }}>Start uit Gripp; oplevering = laatste deliverable-deadline of Gripp-deadline.</Box>
    </Box>
  );
  const midden = 6;
  return (
    <Tip title={tip}>
      <Box sx={{ position: 'relative', height: 12, width: '100%', cursor: 'default' }} onClick={(ev) => ev.stopPropagation()}>
        {/* projectperiode */}
        <Box sx={{ position: 'absolute', left: `${s}%`, width: `${Math.max(1.5, e - s)}%`, top: midden - hoogte / 2, height: hoogte,
          bgcolor: T.balk, borderRadius: 99, overflow: 'hidden' }}>
          <Box sx={{ width: `${e - s > 0 ? (verlopen / (e - s)) * 100 : 0}%`, height: '100%', bgcolor: T.balkVol }} />
        </Box>
        {/* mijlpalen (deliverable-deadlines) — klein en neutraal; details in de tooltip */}
        {mijlpalen.map((m, i) => {
          if (venster && (m.dag < venster.van || m.dag > venster.tot)) return null;
          const p = pos(m.dag);
          return (
            <Box key={`${i}-${m.dag.getTime()}`} sx={{ position: 'absolute', left: `calc(${p}% - 3px)`, top: midden - 3, width: 6, height: 6,
              transform: 'rotate(45deg)', bgcolor: T.surface, border: `1px solid ${T.outline}` }} />
          );
        })}
        {/* vandaag */}
        <Box sx={{ position: 'absolute', left: `calc(${nu}% - 0.5px)`, top: 0, bottom: 0, width: '1px', bgcolor: T.primary, opacity: 0.5 }} />
      </Box>
    </Tip>
  );
}

// Maandlabels boven de gedeelde tijdas in de tabelkop.
export function TijdasKop({ venster }) {
  const bereik = dagenTussen(venster.van, venster.tot);
  const vandaag = vandaagDag();
  const maanden = [];
  const d = new Date(venster.van.getFullYear(), venster.van.getMonth() + 1, 1);
  while (d <= venster.tot) { maanden.push(new Date(d)); d.setMonth(d.getMonth() + 1); }
  const pos = (x) => (dagenTussen(venster.van, x) / bereik) * 100;
  const namen = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  return (
    <Box sx={{ position: 'relative', height: 14, width: '100%' }}>
      {maanden.map((m) => (
        <Typography key={m.getTime()} component="span" sx={{ position: 'absolute', left: `${pos(m)}%`, fontFamily: T.fontBody, fontSize: T.klein, color: T.outlineVariant, pl: 0.4, lineHeight: '14px', textTransform: 'none', letterSpacing: 0 }}>
          {namen[m.getMonth()]}
        </Typography>
      ))}
      <Typography component="span" sx={{ position: 'absolute', left: `${pos(vandaag)}%`, transform: 'translateX(-50%)', fontFamily: T.fontBody, fontSize: T.klein, fontWeight: 700, color: T.primary, lineHeight: '14px', bgcolor: T.surface, px: 0.3, textTransform: 'none', letterSpacing: 0 }}>
        nu
      </Typography>
    </Box>
  );
}

// ── Uren ───────────────────────────────────────────────────────────────────
// Dunne balk zoals in Gripp: groen tot 85 %, oranje tot en met 100 %, rood
// erboven. `kaal`: kleine balk + percentage (standaardtabel); anders met aantallen.
// `onClick`: doorklik naar de uren per persoon op de projectpagina.
export const URENKLEUREN = { groen: T.urenGroen, oranje: T.urenOranje, rood: T.urenRood };

export function UrenBalk({ fractie, breedte = '100%', hoogte = 4 }) {
  const kleur = URENKLEUREN[urenKleur(fractie)] || T.balkVol;
  const pct = Math.max(0, Math.min(100, Math.round((fractie || 0) * 100)));
  return (
    <Box sx={{ width: breedte, height: hoogte, borderRadius: 99, bgcolor: T.balk, overflow: 'hidden', flexShrink: 0 }}>
      <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: kleur }} />
    </Box>
  );
}

export function UrenMeter({ uren, compact = false, kaal = false, onClick }) {
  if (!uren) {
    return (
      <Tip title="Geen urenbudget in Gripp (geen uur- of dagregels, of project niet gekoppeld).">
        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.outlineVariant }}>—</Typography>
      </Tip>
    );
  }
  const pct = Number.isFinite(uren.fractie) ? `${Math.round(uren.fractie * 100)}%` : 'over';
  const tip = `${formatUren(uren.geschreven)} u geschreven van ${formatUren(uren.verkocht)} u begroot${Number.isFinite(uren.fractie) ? '' : ' — dus boven budget'} (Gripp${uren.metDagen ? `; dagregels gerekend als ${UREN_PER_DAG} u` : ''}${uren.handmatig ? '; met handmatig budget uit Declarabiliteit' : ''}).${onClick ? ' Klik voor de uren per persoon.' : ''}`;
  // De rij is een echte link (<a href>): een gewone klik op de meter mag niet ook naar
  // het project navigeren, dus preventDefault. Cmd/ctrl-klik laten we aan de browser.
  const klik = (e) => {
    if (!onClick || e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };
  const klikSx = onClick ? { cursor: 'pointer', '&:hover .uren-tekst': { color: T.text, textDecoration: 'underline' } } : {};
  if (kaal) {
    return (
      <Tip title={tip}>
        <Box onClick={klik} role={onClick ? 'button' : undefined} aria-label={onClick ? 'Uren per persoon' : undefined}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ...klikSx }}>
          <UrenBalk fractie={uren.fractie} breedte={28} />
          <Typography component="span" className="uren-tekst" sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.secondary, whiteSpace: 'nowrap' }}>{pct}</Typography>
        </Box>
      </Tip>
    );
  }
  return (
    <Tip title={tip}>
      <Box sx={{ minWidth: 0, width: '100%', ...klikSx }} onClick={klik} role={onClick ? 'button' : undefined}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, whiteSpace: 'nowrap' }}>
          <Typography component="span" className="uren-tekst" sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.text }}>{pct}</Typography>
          <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline }}>
            {compact ? `${formatUren(uren.geschreven)}/${formatUren(uren.verkocht)} u` : `${formatUren(uren.geschreven)} van ${formatUren(uren.verkocht)} u`}
          </Typography>
        </Box>
        <Box sx={{ mt: 0.4 }}><UrenBalk fractie={uren.fractie} hoogte={3} /></Box>
      </Box>
    </Tip>
  );
}

// ── Taken ──────────────────────────────────────────────────────────────────
export function TakenTeller({ gezondheid, fallbackTotaal }) {
  if (!gezondheid) return <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.outlineVariant }}>…</Typography>;
  const { takenTeLaat, takenOud, openTaken, deliverablesTeLaat } = gezondheid;
  const teLaat = takenTeLaat + deliverablesTeLaat;
  const tip = (
    <Box>
      <div>{openTaken} open taken met een datum{fallbackTotaal ? ` (${fallbackTotaal} taken in totaal)` : ''}</div>
      <div>{takenTeLaat} taken en {deliverablesTeLaat} deliverables te laat (laatste {RECENT_DAGEN} dagen)</div>
      {takenOud > 0 && <div>{takenOud} open taken ouder dan {RECENT_DAGEN} dagen — waarschijnlijk niet afgevinkt</div>}
    </Box>
  );
  return (
    <Tip title={tip}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, whiteSpace: 'nowrap', minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: teLaat ? T.text : T.outlineVariant }}>
          {teLaat} te laat
        </Typography>
        <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline }}>
          / {openTaken} open{takenOud > 0 ? ` · ${takenOud} oud` : ''}
        </Typography>
      </Box>
    </Tip>
  );
}

// ── Volgende deadline ──────────────────────────────────────────────────────
// Neutraal: datum + afstand, eronder wat het is. Urgentie staat in het Signaal.
export function VolgendeDeadline({ gezondheid }) {
  const vandaag = vandaagDag();
  const v = gezondheid?.volgende;
  if (!gezondheid) return <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.outlineVariant }}>…</Typography>;
  if (!v) {
    const eind = gezondheid.eind;
    if (!eind) return <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.outlineVariant }}>—</Typography>;
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.text }}>
          {korteDatum(eind, vandaag)} <Box component="span" sx={{ color: T.outline }}>· {relatief(gezondheid.eindOver)}</Box>
        </Typography>
        <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline }}>oplevering</Typography>
      </Box>
    );
  }
  return (
    <Tip title={`${v.soort === 'deliverable' ? 'Deliverable' : 'Taak'}: ${v.naam} — ${korteDatum(v.dag, vandaag)}${v.deadlineType ? ` (${v.deadlineType})` : ''}`}>
      <Box sx={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
        <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.text }}>
          {korteDatum(v.dag, vandaag)} <Box component="span" sx={{ color: T.outline }}>· {relatief(v.dagenTot)}</Box>
        </Typography>
        <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: T.klein, color: T.outline }}>
          {v.soort === 'deliverable' ? '◆ ' : ''}{v.naam}
        </Typography>
      </Box>
    </Tip>
  );
}

// ── PL ─────────────────────────────────────────────────────────────────────
// Standaard alleen de voornaam in grijs; `avatar` voor de kaart.
export function PlAvatar({ namen = [], avatar = false, size = 20 }) {
  const eerste = namen[0];
  if (!eerste) return <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.outlineVariant }}>geen PL</Typography>;
  return (
    <Tip title={`Projectleider: ${namen.join(', ')}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {avatar && <Avatar sx={{ width: size, height: size, fontSize: size * 0.42, fontWeight: 700, bgcolor: T.surfaceHigh, color: T.secondary, flexShrink: 0 }}>{getInitials(eerste)}</Avatar>}
        <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.secondary }}>
          {String(eerste).split(' ')[0]}{namen.length > 1 ? ` +${namen.length - 1}` : ''}
        </Typography>
      </Box>
    </Tip>
  );
}

// Fase: Gripp-fase (Scriptfase, Productie…) of anders de Airtable-status — als
// gewone grijze tekst, zonder pil.
export function FaseChip({ fase }) {
  if (!fase) return <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: T.normaal, color: T.outlineVariant }}>—</Typography>;
  return (
    <Typography component="span" noWrap sx={{ display: 'block', maxWidth: '100%', fontFamily: T.fontBody, fontSize: T.normaal, color: T.secondary }}>
      {fase === 'In progress' ? 'Lopend' : fase}
    </Typography>
  );
}

// Rijen en kaarten zijn echte links (wiki 26 sep: "project in een nieuw tabblad
// openen"): cmd/ctrl-klik, middelklik en "open in nieuw tabblad" doet de browser
// zelf; een gewone klik blijft binnen de app navigeren.
export const projectHref = (id) => `/projects/${encodeURIComponent(id)}`;
export function opGewoneKlik(e, doe) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  doe();
}
export const linkSx = { color: 'inherit', textDecoration: 'none' };

// Klein driehoekje: postproductie staat vóór de eerste draaidag ingepland
// (wiki 26 sep). Engine: services/postproductie_volgorde.py.
export function PlanningWaarschuwing({ planning }) {
  if (!planning?.aantal) return null;
  const n = planning.aantal;
  const namen = (planning.taken || []).join(', ');
  const titel = (
    <Box>
      <Box sx={{ fontWeight: 700, mb: 0.25 }}>Postproductie vóór de draaidag</Box>
      {n === 1 ? '1 postproductietaak staat' : `${n} postproductietaken staan`} vóór de eerste draaidag
      ({korteDatum(new Date(`${planning.eersteDraaidag}T00:00:00`))}) ingepland{namen ? `: ${namen}` : ''}.
      Verplaats ze naar ná de draaidag.
    </Box>
  );
  return (
    <Tip title={titel}>
      <Box component="span" role="img" aria-label="Postproductie vóór de draaidag"
        sx={{ display: 'inline-flex', verticalAlign: 'middle', ml: 0.5, color: T.oranje, cursor: 'help' }}>
        <Icon name="warning" size={14} />
      </Box>
    </Tip>
  );
}
